import { useCallback, useMemo, useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { PhoneOutlined, PlusCircleOutlined, DownOutlined } from '@ant-design/icons'
import { Button, Checkbox, Flex, Dropdown, Menu } from 'antd'
import { useIsAdmin } from '../../redux/slices/session'
import { ListError, PageHeader } from '../Common'
import CyclesFilter from '../Common/CyclesFilter'
import usePracticeCycles from '../Common/usePracticeCycles'
import useFilters from '../Cycles/useFilters'
import AddNewItemButton from '../Common/AddNewItemButton'
import { toPath } from '../../routes'
import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import { usePreAuthorizations } from '../../redux/slices/pre-authorizations'
import RemindersModal from '../Common/RemindersModal'
import api from '../../api'
import PreAuthorizations from './PreAuthorizations'
import GenerateBillingOrdersButton from './GenerateBillingOrdersButton'
import BatchDownloadButton from './BatchDownloadButton'

const DEFAULT_FILTERS = {
  query: '',
  noFullReports: false,
  authorized: FIELDS_OPTIONS.authorized[0].id,
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    '& > form': {},
  },
  filterContainer: {
    display: 'flex',
    '& > .ant-checkbox-wrapper': {
      alignItems: 'center',
    }
  },
  between: {
    justifyContent: 'space-between',
  },
  mainContainer: {
    '& .page-heading .ant-row .ant-col:last-child': {
      display: 'flex',
      justifyContent: 'flex-end',
    },
  },
  reminder: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  bottom: {
    marginBottom: 20
  }
})

export default function PracticePreAuthorizations() {
  const isAdmin = useIsAdmin()
  const classes = useStyles()
  const [
    { error, pendingCycles, clearError },
    { practice, pending, practiceId },
  ] = usePracticeCycles()
  const { handleFilterChange, handlePageChange, conditions, setConditions } =
    useFilters({ ...DEFAULT_FILTERS, practiceId })
  const preAuthFilters = useMemo(
    () => ({
      q: conditions.query,
      noFullReports: conditions.noFullReports,
      authorized: conditions.authorized,
      officeLocationId: conditions.officeLocationId,
    }),
    [conditions],
  )
  const { cycleId, page } = conditions

  const preAuthorizations = usePreAuthorizations(cycleId)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedPatients, setSelectedPatients] = useState([])
  const hasSelected = selectedRowKeys.length > 0
  const [officeLocations, setOfficeLocations] = useState([])

  useEffect(() => {
    if (practiceId) {
      api.practices.getPracticeOfficeLocations(practiceId).then(data => {
        setOfficeLocations(data)
      })
    }
  }, [practiceId])

  const onSelectChange = rowsIdSelection => {
    setSelectedRowKeys(rowsIdSelection)

    const preAuths = preAuthorizations.filter(pa =>
      rowsIdSelection.includes(pa.id),
    )

    setSelectedPatients(preAuths.map(pa => pa.patient.id))
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  }

  const [remindersOptionsVisible, setRemindersOptionsVisible] = useState(false)
  const hideRemindersOptions = useCallback(
    () => setRemindersOptionsVisible(false),
    [],
  )

  const showRemindersOptions = useCallback(() => {
    setRemindersOptionsVisible(true)
  }, [])

  const showNoFullReports = e => {
    setConditions({ ...conditions, noFullReports: e.target.checked })
  }

  if (cycleId && !pending && !pendingCycles) {
    return (
      <div className={classes.mainContainer}>
        <PageHeader
          className={classes.header}
          title="Remote Authorizations"
          subTitle={isAdmin && practice.name}
          extra={isAdmin && <AddNewItemButton
            key="1"
            itemName="remote authorization"
            path={toPath.cycles.cycle.preAuthorizations.new({
              id: cycleId,
            })}
            icon={<PlusCircleOutlined />}
          />}
        />

        {isAdmin && <Flex gap="10px" justify='flex-end' className={classes.bottom}>
            <BatchDownloadButton key="download" cycleId={cycleId} />
            <GenerateBillingOrdersButton key="1" cycleId={cycleId} />
        </Flex>}

        <Flex className={classes.bottom} gap={10}>
          <CyclesFilter
            practiceId={practiceId}
            onSubmit={handleFilterChange}
            currentCycleId={cycleId}
          />
          <Filters
            defaultValues={DEFAULT_FILTERS}
            currentValues={conditions}
            placeholders={{
              query: 'Search by name or device serial number',
              authorized: 'Authorized status',
            }}
            onSubmit={({ query, authorized, officeLocationId }) =>
              setConditions({
                cycleId,
                query,
                authorized,
                page: 1,
                officeLocationId,
              })
            }
            showSearch
            showAuthorizedStatus
            showOfficeLocation={
              conditions.practiceId && officeLocations.length > 0
            }
            extraFilter={
              <div className={classes.filterContainer}>
                <Checkbox
                  onChange={showNoFullReports}
                  value={conditions.noFullReports}
                >
                  W/O transmission
                </Checkbox>
              </div>
            }
          />
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="1" onClick={showRemindersOptions} disabled={!hasSelected}>
                  <PhoneOutlined /> Send reminder
                </Menu.Item>
              </Menu>
            }
          >
            <Button>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        </Flex>
        <PreAuthorizations
          cycleId={cycleId}
          onPageChange={handlePageChange}
          page={page}
          filters={preAuthFilters}
          rowSelection={rowSelection}
        />
        <RemindersModal
          patientIds={selectedPatients}
          visible={remindersOptionsVisible}
          onCancel={hideRemindersOptions}
        />
        <style>{`
          .page-heading {
            & > .ant-row {
            }
            & .ant-col:last-child {
            }
          }
          .filters-form {
            margin-bottom: 0;
          }
          .ant-form-item {
            margin-bottom: 0 !important;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
    </div>
  )
}
